// MMT-style 15-category hotel filter sidebar + generic filter dispatcher.
// Supports: 'check-list', 'segmented', 'price-buckets', 'custom-range', 'amenity-search',
// 'show-more-list', 'toggle', 'checkbox' (legacy), 'time-bucket' (other modules), 'range' (legacy)

import React, { useMemo, useState } from 'react';
import { ChevronDown, Search as SearchIcon } from 'lucide-react';
import { TIME_BUCKETS } from './searchModules';
import { useCurrency } from '../../context/CurrencyContext';

const FilterSection = ({ title, children, defaultOpen = true, count }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-gray-200 dark:border-gray-800 py-3">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-left text-sm font-semibold text-gray-900 dark:text-white"
      >
        <span>{title}{count != null && count > 0 ? <span className="ml-1.5 text-[10px] text-red-600 dark:text-red-400">({count})</span> : null}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && <div className="mt-3 space-y-2">{children}</div>}
    </div>
  );
};

const Row = ({ checked, label, count, onToggle, testId }) => (
  <label className="flex items-center justify-between gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer hover:text-red-600 dark:hover:text-red-400">
    <span className="flex items-center gap-2 min-w-0">
      <input
        type="checkbox"
        checked={!!checked}
        data-testid={testId}
        onChange={onToggle}
        className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500 shrink-0"
      />
      <span className="truncate">{label}</span>
    </span>
    {count != null && <span className="text-[11px] text-gray-400 dark:text-gray-500 shrink-0">({count})</span>}
  </label>
);

const FilterSidebar = ({ schema, results, values, onChange, embedded = false }) => {
  const { formatPrice } = useCurrency();
  const [amenityQuery, setAmenityQuery] = useState('');
  const [showMore, setShowMore] = useState({}); // per-filter expand flag

  const update = (name, next) => onChange({ ...values, [name]: next });
  const reset = () => onChange({});

  const activeCount = Object.values(values || {}).filter((v) => {
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'boolean') return v;
    if (v && typeof v === 'object') return Object.values(v).some((x) => x != null && x !== '');
    return v != null && v !== '';
  }).length;

  // Legacy range fallback uses these bounds.
  const legacyRangeBounds = useMemo(() => {
    const range = schema.filters.find((f) => f.type === 'range');
    if (!range || !results?.length) return [0, 0];
    const key = range.key;
    const xs = results.map((r) => Number(r[key])).filter((n) => !isNaN(n));
    return xs.length ? [Math.floor(Math.min(...xs)), Math.ceil(Math.max(...xs))] : [0, 0];
  }, [schema, results]);

  return (
    <aside className={`bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 ${embedded ? '' : 'sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto'}`} data-testid="filter-sidebar">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-bold text-gray-900 dark:text-white">Filters</h3>
        {activeCount > 0 && (
          <button onClick={reset} data-testid="clear-filters-btn" className="text-xs text-red-600 dark:text-red-400 hover:underline">
            Clear all ({activeCount})
          </button>
        )}
      </div>

      {schema.filters.map((f) => {
        const sel = values[f.name];

        // ─── check-list (multi-select w/ counts) ────────────────────────
        if (f.type === 'check-list') {
          const opts = f.options(results || []);
          if (!opts.length) return null;
          const selected = Array.isArray(sel) ? sel : [];
          return (
            <FilterSection key={f.name} title={f.label} count={selected.length}>
              <div className="space-y-1.5">
                {opts.map((o) => (
                  <Row
                    key={o.id}
                    checked={selected.includes(o.id)}
                    label={o.label}
                    count={o.count}
                    testId={`filter-${f.name}-${o.id}`}
                    onToggle={() => {
                      const next = selected.includes(o.id) ? selected.filter((x) => x !== o.id) : [...selected, o.id];
                      update(f.name, next);
                    }}
                  />
                ))}
              </div>
            </FilterSection>
          );
        }

        // ─── segmented (single-pick toggle) ──────────────────────────────
        if (f.type === 'segmented') {
          const opts = f.options(results || []);
          const current = sel || opts[0]?.id;
          return (
            <FilterSection key={f.name} title={f.label}>
              <div className="grid grid-cols-2 gap-2">
                {opts.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    data-testid={`filter-${f.name}-${o.id}`}
                    onClick={() => update(f.name, o.id)}
                    className={`text-xs py-2 px-2 rounded-md border transition-colors ${
                      current === o.id
                        ? 'border-red-500 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 font-semibold'
                        : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-red-300'
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </FilterSection>
          );
        }

        // ─── price-buckets (multi-select ranges w/ counts) ───────────────
        if (f.type === 'price-buckets') {
          const opts = f.options(results || []);
          if (!opts.length) return null;
          const selected = Array.isArray(sel) ? sel : [];
          // Inline custom-range support (Custom Budget under Price Per Night, after the buckets)
          const cr = f.customRange;
          const crVal = (cr && values[cr.name]) || {};
          return (
            <FilterSection key={f.name} title={f.label} count={selected.length}>
              <div className="space-y-1.5">
                {opts.map((o) => (
                  <Row
                    key={o.id}
                    checked={selected.includes(o.id)}
                    label={o.label}
                    count={o.count}
                    testId={`filter-${f.name}-${o.id}`}
                    onToggle={() => {
                      const next = selected.includes(o.id) ? selected.filter((x) => x !== o.id) : [...selected, o.id];
                      update(f.name, next);
                    }}
                  />
                ))}
              </div>
              {cr && (
                <div className="mt-3 pt-3 border-t border-dashed border-gray-200 dark:border-gray-700">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5">Custom Budget</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      placeholder="Min ₹"
                      value={crVal.min ?? ''}
                      data-testid={`filter-${cr.name}-min`}
                      onChange={(e) => update(cr.name, { ...crVal, min: e.target.value })}
                      className="w-full h-8 text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 rounded-md px-2"
                    />
                    <span className="text-gray-400">–</span>
                    <input
                      type="number"
                      placeholder="Max ₹"
                      value={crVal.max ?? ''}
                      data-testid={`filter-${cr.name}-max`}
                      onChange={(e) => update(cr.name, { ...crVal, max: e.target.value })}
                      className="w-full h-8 text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 rounded-md px-2"
                    />
                  </div>
                  {((crVal.min !== '' && crVal.min != null) || (crVal.max !== '' && crVal.max != null)) && (
                    <button
                      type="button"
                      data-testid={`filter-${cr.name}-clear`}
                      onClick={() => update(cr.name, {})}
                      className="text-[11px] text-red-600 dark:text-red-400 font-semibold hover:underline mt-2"
                    >Clear custom budget</button>
                  )}
                </div>
              )}
            </FilterSection>
          );
        }

        // ─── custom-range (min/max input pair) ──────────────────────────
        if (f.type === 'custom-range') {
          const val = sel || {};
          return (
            <FilterSection key={f.name} title={f.label} defaultOpen={false}>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={val.min || ''}
                  data-testid={`filter-${f.name}-min`}
                  onChange={(e) => update(f.name, { ...val, min: e.target.value })}
                  className="w-full h-8 text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 rounded-md px-2"
                />
                <span className="text-gray-400">–</span>
                <input
                  type="number"
                  placeholder="Max"
                  value={val.max || ''}
                  data-testid={`filter-${f.name}-max`}
                  onChange={(e) => update(f.name, { ...val, max: e.target.value })}
                  className="w-full h-8 text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 rounded-md px-2"
                />
              </div>
              <button
                type="button"
                data-testid={`filter-${f.name}-clear`}
                onClick={() => update(f.name, {})}
                className="text-[11px] text-red-600 dark:text-red-400 font-semibold hover:underline mt-2"
              >Clear custom budget</button>
            </FilterSection>
          );
        }

        // ─── amenity-search (searchable check-list) ──────────────────────
        if (f.type === 'amenity-search') {
          const opts = f.options(results || []);
          const filtered = amenityQuery
            ? opts.filter((o) => o.label.toLowerCase().includes(amenityQuery.toLowerCase()))
            : opts;
          const selected = Array.isArray(sel) ? sel : [];
          const expanded = showMore[f.name];
          const view = expanded ? filtered : filtered.slice(0, 8);
          return (
            <FilterSection key={f.name} title={f.label} count={selected.length}>
              <div className="relative mb-2">
                <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search amenities"
                  data-testid={`filter-${f.name}-search`}
                  value={amenityQuery}
                  onChange={(e) => setAmenityQuery(e.target.value)}
                  className="w-full h-8 pl-8 pr-2 text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 rounded-md"
                />
              </div>
              <div className="space-y-1.5">
                {view.map((o) => (
                  <Row
                    key={o.id}
                    checked={selected.includes(o.id)}
                    label={o.label}
                    count={o.count}
                    testId={`filter-${f.name}-${o.id.replace(/\s+/g, '-').toLowerCase()}`}
                    onToggle={() => {
                      const next = selected.includes(o.id) ? selected.filter((x) => x !== o.id) : [...selected, o.id];
                      update(f.name, next);
                    }}
                  />
                ))}
              </div>
              {filtered.length > 8 && (
                <button
                  type="button"
                  data-testid={`filter-${f.name}-more`}
                  onClick={() => setShowMore((s) => ({ ...s, [f.name]: !expanded }))}
                  className="mt-2 text-[11px] text-red-600 dark:text-red-400 font-semibold hover:underline"
                >{expanded ? 'Show less ↑' : `Show more (${filtered.length - 8}) ↓`}</button>
              )}
            </FilterSection>
          );
        }

        // ─── show-more-list (locations/chains with show-more) ───────────
        if (f.type === 'show-more-list') {
          const opts = f.options(results || []);
          if (!opts.length) return null;
          const selected = Array.isArray(sel) ? sel : [];
          const expanded = showMore[f.name];
          const view = expanded ? opts : opts.slice(0, 6);
          return (
            <FilterSection key={f.name} title={f.label} count={selected.length}>
              <div className="space-y-1.5">
                {view.map((o) => (
                  <Row
                    key={o.id}
                    checked={selected.includes(o.id)}
                    label={o.label}
                    count={o.count}
                    testId={`filter-${f.name}-${String(o.id).replace(/\s+/g, '-').toLowerCase()}`}
                    onToggle={() => {
                      const next = selected.includes(o.id) ? selected.filter((x) => x !== o.id) : [...selected, o.id];
                      update(f.name, next);
                    }}
                  />
                ))}
              </div>
              {opts.length > 6 && (
                <button
                  type="button"
                  data-testid={`filter-${f.name}-more`}
                  onClick={() => setShowMore((s) => ({ ...s, [f.name]: !expanded }))}
                  className="mt-2 text-[11px] text-red-600 dark:text-red-400 font-semibold hover:underline"
                >{expanded ? 'Show less ↑' : `Show more (${opts.length - 6}) ↓`}</button>
              )}
            </FilterSection>
          );
        }

        // ─── toggle (single switch w/ count badge) ──────────────────────
        if (f.type === 'toggle') {
          const opt = f.options ? f.options(results || []) : { count: undefined };
          const on = !!sel;
          return (
            <FilterSection key={f.name} title={f.label}>
              <label className="flex items-center justify-between cursor-pointer text-sm text-gray-700 dark:text-gray-300">
                <span className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={on}
                    data-testid={`filter-${f.name}`}
                    onChange={(e) => update(f.name, e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                  />
                  Enable
                </span>
                {opt.count != null && <span className="text-[11px] text-gray-400 dark:text-gray-500">({opt.count})</span>}
              </label>
            </FilterSection>
          );
        }

        // ─── LEGACY: simple checkbox (used by non-hotel modules) ──────
        if (f.type === 'checkbox') {
          const opts = f.options(results || []) || [];
          if (opts.length === 0) return null;
          const selected = Array.isArray(sel) ? sel : [];
          return (
            <FilterSection key={f.name} title={f.label} count={selected.length}>
              <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                {opts.map((o) => (
                  <Row
                    key={o}
                    checked={selected.includes(o)}
                    label={o}
                    testId={`filter-${f.name}-${String(o).replace(/\s+/g, '-')}`}
                    onToggle={() => {
                      const next = selected.includes(o) ? selected.filter((x) => x !== o) : [...selected, o];
                      update(f.name, next);
                    }}
                  />
                ))}
              </div>
            </FilterSection>
          );
        }

        // ─── LEGACY: time-bucket ────────────────────────────────────────
        if (f.type === 'time-bucket') {
          const selected = Array.isArray(sel) ? sel : [];
          return (
            <FilterSection key={f.name} title={f.label}>
              <div className="grid grid-cols-2 gap-2">
                {TIME_BUCKETS.map((b) => {
                  const checked = selected.includes(b.id);
                  return (
                    <button
                      type="button"
                      key={b.id}
                      data-testid={`filter-${f.name}-${b.id}`}
                      onClick={() => {
                        const next = checked ? selected.filter((x) => x !== b.id) : [...selected, b.id];
                        update(f.name, next);
                      }}
                      className={`text-xs py-2 px-2 rounded-md border transition-colors ${
                        checked
                          ? 'border-red-500 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 font-semibold'
                          : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-red-300'
                      }`}
                    >
                      {b.label}
                    </button>
                  );
                })}
              </div>
            </FilterSection>
          );
        }

        // ─── LEGACY: range slider ───────────────────────────────────────
        if (f.type === 'range') {
          const [lo, hi] = legacyRangeBounds;
          if (lo === hi) return null;
          const [vLo, vHi] = sel || [lo, hi];
          return (
            <FilterSection key={f.name} title={f.label}>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                  <span>{formatPrice(vLo ?? lo)}</span>
                  <span>{formatPrice(vHi ?? hi)}</span>
                </div>
                <input
                  type="range"
                  min={lo}
                  max={hi}
                  step={Math.max(1, Math.round((hi - lo) / 100))}
                  value={vHi ?? hi}
                  data-testid={`filter-${f.name}-max`}
                  onChange={(e) => update(f.name, [vLo ?? lo, Number(e.target.value)])}
                  className="w-full accent-red-600"
                />
              </div>
            </FilterSection>
          );
        }

        return null;
      })}

      {/* Back to top */}
      <div className="pt-3 mt-2 border-t border-gray-200 dark:border-gray-800">
        <button
          type="button"
          data-testid="filters-back-to-top"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="w-full text-xs text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 font-semibold py-2"
        >
          ↑ Back to top
        </button>
      </div>
    </aside>
  );
};

export default FilterSidebar;
